import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, TicketIcon, MessageSquare, User, Clock, AlertCircle, CheckCircle, Filter, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { ClientSearchSelect } from '@/components/ClientSearchSelect';

export default function Tickets({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  
  const [formData, setFormData] = useState({
    customer_id: '',
    subject: '',
    description: '',
    category_id: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTickets();
    fetchCategories();
    
    // Si viene de detalle de cliente, abrir diálogo con cliente pre-seleccionado
    if (location.state?.customerId) {
      setFormData(prev => ({
        ...prev,
        customer_id: location.state.customerId
      }));
      setCreateDialogOpen(true);
      // Limpiar el state para que no se abra cada vez
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const fetchTickets = async () => {
    try {
      // Use the SLA status endpoint to get tickets with SLA information
      const response = await axiosInstance.get('/tickets/sla-status');
      setTickets(response.data);
    } catch (error) {
      toast.error('Error al cargar tickets');
    }
  };

  // Ya no se descarga ninguna lista de clientes: el filtro los busca contra
  // el servidor segun escribes. Antes se pedian `/customers` sin parametros,
  // o sea los 50 primeros de 2.044.

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/ticket-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      // Optimistic update: prepend el ticket creado al state sin esperar al GET.
      const { data: created } = await axiosInstance.post('/tickets', formData);
      setTickets((prev) => [created, ...prev]);
      toast.success('Ticket creado correctamente');
      setCreateDialogOpen(false);
      setFormData({ customer_id: '', subject: '', description: '', category_id: '', priority: 'medium' });
      // Safety re-fetch por si el backend anade metadata (SLA, asignacion, etc.)
      fetchTickets();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error al crear ticket');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: <Badge className="bg-blue-100 text-blue-800">📥 Nuevo</Badge>,
      waiting_client: <Badge className="bg-yellow-100 text-yellow-800">⏸️ Espera Cliente</Badge>,
      waiting_tech: <Badge className="bg-orange-100 text-orange-800">👷 En Progreso</Badge>,
      third_party: <Badge className="bg-purple-100 text-purple-800">🔗 Terceros</Badge>,
      closed: <Badge className="bg-gray-100 text-gray-800">✅ Cerrado</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: <Badge className="bg-red-600 text-white">🔴 URGENTE</Badge>,
      high: <Badge className="bg-orange-500 text-white">🟠 Alta</Badge>,
      medium: <Badge className="bg-yellow-500 text-white">🟡 Media</Badge>,
      low: <Badge className="bg-green-500 text-white">🟢 Baja</Badge>
    };
    return badges[priority] || <Badge>{priority}</Badge>;
  };

  // Filtrar tickets
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = searchTerm === '' || 
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    const matchesCategory = filterCategory === 'all' || ticket.category_id === filterCategory;
    const matchesCustomer = filterCustomer === 'all' || ticket.customer_id === filterCustomer;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory && matchesCustomer;
  });

  // Get SLA badge based on status
  const getSLABadge = (ticket) => {
    if (!ticket.sla_info || !ticket.sla_due_date || ticket.status === 'closed') {
      return null;
    }

    const { status, remaining_minutes } = ticket.sla_info;

    // Format remaining time
    let timeText = '';
    if (remaining_minutes < 0) {
      const overdue = Math.abs(remaining_minutes);
      if (overdue < 60) {
        timeText = `Vencido hace ${overdue}m`;
      } else if (overdue < 1440) {
        timeText = `Vencido hace ${Math.floor(overdue / 60)}h`;
      } else {
        timeText = `Vencido hace ${Math.floor(overdue / 1440)}d`;
      }
    } else {
      if (remaining_minutes < 60) {
        timeText = `Vence en ${remaining_minutes}m`;
      } else if (remaining_minutes < 1440) {
        timeText = `Vence en ${Math.floor(remaining_minutes / 60)}h`;
      } else {
        timeText = `Vence en ${Math.floor(remaining_minutes / 1440)}d`;
      }
    }

    const statusConfig = {
      breached: {
        className: 'bg-red-600 text-white border-red-700',
        icon: AlertCircle,
        label: timeText
      },
      critical: {
        className: 'bg-red-500 text-white border-red-600',
        icon: AlertTriangle,
        label: timeText
      },
      warning: {
        className: 'bg-yellow-500 text-white border-yellow-600',
        icon: Clock,
        label: timeText
      },
      ok: {
        className: 'bg-green-500 text-white border-green-600',
        icon: CheckCircle,
        label: timeText
      }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const Icon = config.icon;

    return (
      <Badge className={`${config.className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-[#212B35]">🎫 Sistema de Tickets</h1>
            <p className="text-[#212B35]/70 mt-2">Gestión de incidencias y soporte</p>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#9C0F17] hover:bg-[#7A0C12] w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Ticket
          </Button>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="search-tickets">Buscar</Label>
                <Input
                  id="search-tickets"
                  placeholder="#TKT, cliente o asunto"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="filter-customer">Cliente</Label>
                {/* Buscador contra el servidor, no un desplegable: hay 2.044
                    clientes y aqui se pedian los 50 primeros, asi que filtrar
                    por uno que no estuviera entre esos 50 era imposible.
                    Es el MISMO componente que ya usaba el formulario de crear
                    ticket, unas lineas mas abajo. */}
                <div className="mt-1">
                  <ClientSearchSelect
                    label={null}
                    value={filterCustomer === 'all' ? null : filterCustomer}
                    onValueChange={(id) => setFilterCustomer(id || 'all')}
                    textoVacio="Todos los clientes — escribe para filtrar por uno"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="filter-status">Estado</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger id="filter-status" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="open">Nuevo</SelectItem>
                    <SelectItem value="waiting_tech">En Progreso</SelectItem>
                    <SelectItem value="waiting_client">Espera Cliente</SelectItem>
                    <SelectItem value="closed">Cerrado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-priority">Prioridad</Label>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger id="filter-priority" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="medium">Media</SelectItem>
                    <SelectItem value="low">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-category">Categoría</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger id="filter-category" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              Mostrando {filteredTickets.length} de {tickets.length} tickets
            </div>
          </CardContent>
        </Card>

        {/* Lista de Tickets */}
        <div className="grid grid-cols-1 gap-4">
          {filteredTickets.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <TicketIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-600">
                  {tickets.length === 0 ? 'No hay tickets aún.' : 'No se encontraron tickets con los filtros aplicados.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredTickets.map((ticket) => (
              <Card 
                key={ticket.id} 
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/tickets/${ticket.id}`)}
              >
                <CardHeader>
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div className="flex-1 w-full">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-mono text-sm text-gray-600">{ticket.ticket_number}</span>
                        {getPriorityBadge(ticket.priority)}
                        {getStatusBadge(ticket.status)}
                        {ticket.category_name && (
                          <Badge style={{ backgroundColor: ticket.category_color }}>
                            {ticket.category_name}
                          </Badge>
                        )}
                        {getSLABadge(ticket)}
                      </div>
                      <CardTitle className="text-lg">{ticket.subject}</CardTitle>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {ticket.customer_name}
                        </div>
                        {ticket.assigned_to_name && (
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            Asignado: {ticket.assigned_to_name}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(ticket.created_at).toLocaleString('es-ES')}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))
          )}
        </div>

        {/* Diálogo de Crear Ticket */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Ticket</DialogTitle>
              <DialogDescription>
                Registra una nueva incidencia o solicitud de soporte
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <ClientSearchSelect
                label="Cliente"
                value={formData.customer_id}
                onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                required={true}
              />

              <div>
                <Label htmlFor="category">Categoría</Label>
                <Select 
                  value={formData.category_id} 
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="priority">Prioridad *</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">🟢 Baja</SelectItem>
                    <SelectItem value="medium">🟡 Media</SelectItem>
                    <SelectItem value="high">🟠 Alta</SelectItem>
                    <SelectItem value="urgent">🔴 Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="subject">Asunto *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Resumen del problema"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descripción *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe el problema en detalle..."
                  rows={6}
                  required
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                >
                  Crear Ticket
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}